import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import {
    Layout,
    Typography,
    Button,
    Space,
    Table,
    Input,
    Switch,
    Modal,
    Tag,
    Spin,
    message,
    Popconfirm,
    Avatar,
    Upload,
    Row, 
    Col, 
    Select
} from 'antd';
import {
    ArrowLeftOutlined,
    EditOutlined,
    DeleteOutlined,
    SaveOutlined,
    CloseOutlined,
    UserAddOutlined,
    LockOutlined,
    ImportOutlined,
    ClockCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import useGroupDetailStore from '../../stores/useGroupDetailStore';
import formatterUtil from '../../utils/format';
import moment from 'moment';
import _ from 'lodash';
import UserAvatar from '../common/UserAvatar';
import AddContactsModal from './AddContactsModal';
import ImportContactsModal from '../contacts/ImportContactsModal';
import PhoneDisplay from '../common/PhoneDisplay';

const { Text, Title } = Typography;
const { Option } = Select;

const ViewGroupV2 = ({ match, history }) => {
    const groupId = match.params.groupId;

    const {
        group,
        loading,
        contacts,
        contactsLoading,
        pagination,
        sort,
        isEditing,
        editedName,
        editedPrivacy,
        addContactsModalOpen,
        deleteModalOpen,
        searchLoading,
        setIsEditing,
        setEditedName,
        setEditedPrivacy,
        setAddContactsModalOpen,
        setDeleteModalOpen,
        setSelectedContactIds,
        setSort,
        fetchGroup,
        fetchContacts,
        updateGroup,
        deleteGroup,
        archiveGroup,
        removeContact,
        addSelectedContacts,
        reset
    } = useGroupDetailStore();

    // Import Modal State
    const [importModalVisible, setImportModalVisible] = useState(false);

    // Initial load
    useEffect(() => {
        if (groupId) {
            reset();
            fetchGroup(groupId).catch(() => {
                history.replace('/groups');
            });
            fetchContacts(groupId);
        }
        return () => reset();
    }, [groupId]);

    // Check for auto-open actions from CreateGroupV2
    useEffect(() => {
        if (history.location.state) {
            const { openImport, openAddExisting } = history.location.state;
            if (openImport) {
                setImportModalVisible(true);
            }
            if (openAddExisting) {
                setAddContactsModalOpen(true);
            }
            // Clear state to prevent re-opening on refresh/navigation?
            // React Router history state persists, so we might want to clear it.
            // However, modifying history during render/effect might be tricky.
            // For now, let's leave it, or use history.replace() to clear it.
            if (openImport || openAddExisting) {
                history.replace({ ...history.location, state: {} });
            }
        }
    }, [history.location.state]);

    const handleTableChange = (pag, _tableFilters, sorter) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const nextField = single && single.order ? (single.field || single.columnKey) : 'name';
        const nextOrder = single && single.order === 'descend' ? 'desc' : 'asc';
        const sortChanged = nextField !== sort.field || nextOrder !== sort.order;
        if (sortChanged) {
            setSort(nextField, nextOrder);
            // When sort changes, jump back to page 1 so the user sees the top of the new order.
            fetchContacts(groupId, 1, pag.pageSize);
        } else {
            fetchContacts(groupId, pag.current, pag.pageSize);
        }
    };

    const getSortOrder = (field) => {
        if (sort.field !== field) return null;
        return sort.order === 'asc' ? 'ascend' : 'descend';
    };

    const handleDelete = () => {
        deleteGroup(groupId, () => {
            history.replace('/groups');
        });
    };

    const handleSave = () => {
        updateGroup(groupId);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const handleAddContacts = async (selectedObjects) => {
        // Extract IDs
        const ids = selectedObjects.map(c => c.id);
        
        // Use store action
        setSelectedContactIds(ids);
        await addSelectedContacts(groupId);
    };

    const handleImportFinish = () => {
        fetchContacts(groupId);
    };


    // Contacts table columns
    const contactColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 260,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('name'),
            defaultSortOrder: 'ascend',
            render: (text, record) => (
                <a
                    className="gh-table-link"
                    onClick={() => history.push(`/contacts/${record.id}`)}
                    title={text}
                    style={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle'
                    }}
                >
                    {text}
                </a>
            )
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            render: (mobile) => <PhoneDisplay value={mobile} />
        },
        {
            title: 'Email',
            key: 'email',
            dataIndex: 'email',
            render: (email) => email || '--'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            sortOrder: getSortOrder('status'),
            render: (status) => status || '--'
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('notes'),
            render: (notes) => notes || '--'
        },
        {
            title: '',
            key: 'action',
            align: 'right',
            render: (_, record) => (
                <Popconfirm
                    title="Remove from group?"
                    description="This won't delete the contact, just remove them from this group."
                    onConfirm={() => removeContact(groupId, record.id)}
                    okText="Remove"
                    cancelText="Cancel"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    if (loading && !group) {
        return (
            <div style={{ minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#FAFAFA' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="gh-compact-page" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="gh-compact-panel" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <div
                                    style={{
                                        marginBottom: 16,
                                        background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                                        border: '1px solid #d9f7be',
                                        padding: 20
                                    }}
                                >
                                    <div style={{ marginBottom: 12 }}>
                                        <Button
                                            type="text"
                                            icon={<ArrowLeftOutlined />}
                                            onClick={() => history.push('/groups')}
                                            style={{ padding: 0 }}
                                        >
                                            Back to Groups
                                        </Button>
                                    </div>
                                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size={6}>
                                                {isEditing ? (
                                                    <Input
                                                        value={editedName}
                                                        onChange={(e) => setEditedName(e.target.value)}
                                                        style={{ fontSize: 24, fontWeight: 600, width: '100%', maxWidth: 400 }}
                                                        placeholder="Group name"
                                                    />
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <Title level={3} style={{ margin: 0, color: '#1f2937' }}>{group?.name}</Title>
                                                        {group?.privacy === 'private' && (
                                                            <Tag icon={<LockOutlined />} color="warning">Private</Tag>
                                                        )}
                                                    </div>
                                                )}
                                                <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                                    Manage members of this group, track engagement, and update segment criteria.
                                                </Text>
                                                <Space size={16}>
                                                    <Space size={6}>
                                                        <UserOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">{group?.contact_count || 0} group members</Text>
                                                    </Space>
                                                    <Space size={6}>
                                                        <ClockCircleOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">Created: {group?.created_at ? moment(group.created_at).format('MMM DD, YYYY') : '--'}</Text>
                                                    </Space>
                                                </Space>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                            <Space wrap>
                                                {isEditing ? (
                                                    <>
                                                        <Button onClick={handleCancelEdit} icon={<CloseOutlined />}>
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            onClick={handleSave}
                                                            icon={<SaveOutlined />}
                                                            loading={loading}
                                                            style={{ backgroundColor: '#4CAF50', borderColor: '#4CAF50' }}
                                                        >
                                                            Save
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button onClick={() => setIsEditing(true)} icon={<EditOutlined />}>
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            danger
                                                            onClick={() => setDeleteModalOpen(true)}
                                                            icon={<DeleteOutlined />}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </>
                                                )}
                                            </Space>
                                        </Col>
                                    </Row>
                                    {/* Privacy toggle - only visible when editing and user is owner */}
                                    {isEditing && group?.canEditPrivacy && (
                                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}> 
                                            <Text>Privacy:</Text>
                                            <Switch
                                                checked={editedPrivacy === 'private'}
                                                onChange={(checked) => setEditedPrivacy(checked ? 'private' : 'public')}
                                                checkedChildren="Private"
                                                unCheckedChildren="Public"
                                            />
                                            {!group?.canEditPrivacy && (
                                                <Text type="secondary">(Only the group owner can change privacy)</Text>
                                            )}
                                        </div>
                                    )}
                                </div>

            {/* Main Content */}
            <div style={{ flex: 1, margin: '0', backgroundColor: 'white', display: 'flex', flexDirection: 'column' }}>
                {/* Toolbar */}
                <div className="gh-compact-toolbar" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <Text strong style={{ fontSize: 16 }}>Contacts</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            ({pagination.total} total)
                        </Text>
                    </div>
                    <Space>
                        <Button icon={<UserAddOutlined />} onClick={() => setAddContactsModalOpen(true)}>Add Existing</Button>
                        <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>Import</Button>
                    </Space>
                </div>

                {/* Table */}
                <div className="gh-compact-table-wrap">
                    <Table
                        className="gh-themed-table"
                        columns={contactColumns}
                        dataSource={contacts}
                        rowKey="id"
                        loading={contactsLoading}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts`
                        }}
                        onChange={handleTableChange}
                        size="small"
                    />
                </div>
            </div>

            {/* Add Contacts Modal */}
            <AddContactsModal 
                visible={addContactsModalOpen}
                onCancel={() => setAddContactsModalOpen(false)}
                onAdd={handleAddContacts}
                excludeContactIds={contacts.map(c => c.id)}
                confirmLoading={searchLoading}
            />

            {/* Import Modal */}
            <ImportContactsModal 
                visible={importModalVisible}
                onCancel={() => setImportModalVisible(false)}
                onFinish={handleImportFinish}
                mode="execute"
                targetGroupId={groupId}
                availableGroups={group ? [group] : []}
            />

            {/* Delete/Archive Confirmation Modal */}
            <Modal
                title="Delete or Archive Group"
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setDeleteModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button 
                        key="archive" 
                        onClick={() => archiveGroup(groupId, () => history.replace('/groups'))}
                        loading={loading}
                    >
                        Archive
                    </Button>,
                    <Button 
                        key="delete" 
                        danger 
                        type="primary" 
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Delete
                    </Button>
                ]}
            >
                <p>What would you like to do with <strong>{group?.name}</strong>?</p>
                <p><strong>Archive:</strong> Hides the group but keeps data intact. Can be restored later.</p>
                <p><strong>Delete:</strong> Permanently removes the group. This cannot be undone.</p>
                <p style={{ marginTop: 8, color: '#888' }}>Contacts in this group will not be affected.</p>
            </Modal>
            </div>
        </div>
    );
};

export default withRouter(ViewGroupV2);
